import { FocusScope } from '@radix-ui/react-focus-scope';
import { useEffect, useRef } from 'react';

import { cn } from '@/components/ui';
import { auditorMediaQueries } from '@/config';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import DrawerContent from './DrawerContent';
import DrawerHeader from './DrawerHeader';

interface Props {
  open: boolean;
  window?: () => Window;
  handleDrawerToggle?: () => void;
}

const MainDrawer = ({ open, handleDrawerToggle }: Props) => {
  const compact = useMediaQuery(auditorMediaQueries.downLg);
  const phone = useMediaQuery(auditorMediaQueries.downSm);
  const overlayOpen = open && compact;
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!overlayOpen) {
      return undefined;
    }

    restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDrawerToggle?.();
      }
    };
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('keydown', closeOnEscape);
      restoreFocusRef.current?.focus?.();
      restoreFocusRef.current = null;
    };
  }, [handleDrawerToggle, overlayOpen]);

  const panel = (
    <>
      <DrawerHeader open={open} />
      <DrawerContent />
    </>
  );

  // A phone has no persistent icon rail. Navigation is the overlay, opened from the header.
  if (phone && !open) {
    return null;
  }

  return (
    <nav aria-label="Primary navigation">
      {overlayOpen && (
        <button
          type="button"
          aria-label="Close navigation overlay"
          className="fixed inset-0 z-header cursor-default bg-ink/40 animate-drawer-backdrop motion-reduce:animate-none"
          onClick={handleDrawerToggle}
        />
      )}
      {overlayOpen ? (
        <FocusScope
          trapped
          loop
          className={cn(
            'fixed inset-y-0 left-0 z-nav flex w-drawer flex-col overflow-hidden',
            'bg-sidebar text-sidebar-ink shadow-auditor-overlay overscroll-contain animate-drawer-enter motion-reduce:animate-none',
          )}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Primary navigation"
            className="flex h-full flex-col overflow-hidden"
          >
            {panel}
          </div>
        </FocusScope>
      ) : (
        <aside
          className={cn(
            // Clip a full-width inner panel. Labels live past the mini-rail column so
            // open/close only changes the clip — no mid-animation remount or reflow.
            'fixed inset-y-0 left-0 z-nav overflow-hidden',
            'bg-sidebar text-sidebar-ink transition-drawer duration-300 ease-drawer motion-reduce:transition-none',
            open ? 'w-drawer' : 'w-mini-drawer',
          )}
        >
          <div className="flex h-full w-drawer flex-col overflow-x-hidden">{panel}</div>
        </aside>
      )}
    </nav>
  );
};

export default MainDrawer;
